import { React, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import bgImage from "assets/images/background_image_bzu.webp";
import CircularProgress from '@mui/material/CircularProgress';
import { useParams } from "react-router-dom";
import { set } from 'date-fns';
import { ToastContainer, toast } from "react-toastify";
import logo from "assets/images/Logo-BZU.png";
import BasicLayoutR from "layouts/authentication/components/BasicLayoutR";
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
const BASE_URL = process.env.REACT_APP_BASE_URL;

function StudentRegister() {
  const navigate = useNavigate();
  const organization_id = "1";
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cnic, setCnic] = useState('');
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const validatePhone = (value) => {
    const phoneRegex = /^03\d{9}$/;
    return phoneRegex.test(value);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCnic = (cnic) => {
    // Remove dashes and check if it's exactly 13 digits
    const cleanedCnic = cnic.replace(/-/g, '');
    return cleanedCnic.length === 13 && /^\d+$/.test(cleanedCnic);
  };

  const validateForm = () => {
    const errors = {};

    // Check required fields
    if (!first_name.trim()) {
      errors.first_name = "First name is required";
    }

    if (!last_name.trim()) {
      errors.last_name = "Last name is required";
    }

    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!cnic.trim()) {
      errors.cnic = "CNIC is required";
    } else if (!validateCnic(cnic)) {
      errors.cnic = "CNIC must be 13 digits in format XXXXX-XXXXXXX-X";
    }

    if (!phone.trim()) {
      errors.phone = "WhatsApp number is required";
    } else if (!validatePhone(phone)) {
      errors.phone = "WhatsApp number must start with 03 and be 11 digits long";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    // Remove all non-numeric characters
    value = value.replace(/\D/g, '');

    // Limit input to 11 digits
    if (value.length > 11) {
      value = value.slice(0, 11);
    }

    setPhone(value);

    // Clear phone error when user starts typing
    if (formErrors.phone) {
      setFormErrors(prev => ({ ...prev, phone: '' }));
    }

    // Only show error if input is non-empty and either:
    // - doesn't start with "03"
    // - is less than 11 digits
    const isValid = value.startsWith('03') && value.length === 11;
    setPhoneError(value.length > 0 && !isValid);
  };

  // Function to format CNIC with dashes
  const formatCnic = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');

    // Insert dashes at appropriate positions
    let formatted = cleaned;
    if (cleaned.length > 5) {
      formatted = cleaned.substring(0, 5) + '-' + cleaned.substring(5);
    }
    if (cleaned.length > 12) {
      formatted = formatted.substring(0, 13) + '-' + formatted.substring(13);
    }

    // Limit to the correct length (15 characters including dashes)
    return formatted.substring(0, 15);
  };

  // Handle CNIC input change
  const handleCnicChange = (e) => {
    const formattedCnic = formatCnic(e.target.value);
    setCnic(formattedCnic);

    // Clear CNIC error when user starts typing
    if (formErrors.cnic) {
      setFormErrors(prev => ({ ...prev, cnic: '' }));
    }
  };

  // Clear form errors when user starts typing in other fields
  const handleInputChange = (field, value, setter) => {
    setter(value);
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    navigate('/'); // Redirect to login page
  };

  const registerStudent = async () => {
    // Validate form before making API call
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    // Additional phone validation check
    if (phoneError) {
      toast.error("Please enter a valid phone number starting with 03 and 11 digits long.");
      return;
    }

    setLoading(true);

    // Prepare the data payload
    const data = {
      first_name: first_name.trim(),
      last_name: last_name.trim(),
      email: email.trim().toLowerCase(),
      organization_id: organization_id,
      phone: phone.trim(),
      cnic: cnic.trim(),
    };

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    };

    try {
      // Call the API
      const response = await fetch(`${BASE_URL}/auth/register_applicant`, requestOptions);

      // Parse the response JSON
      const result = await response.json();

      // Handle different response statuses
      if (response.ok && result.success) {
        setSuccessMessage(result.message || "Registration successful. Credentials sent to your email");
        setOpenModal(true);
      } else {
        const errorMessage = result.error || "Registration failed. Please try again.";
        toast.error(errorMessage);
      }
    } catch (error) {
      // Handle fetch errors
      console.error("Error during registration:", error);
      toast.error("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '10px',
    p: 4,
    textAlign: 'center',
  };

  return (
    <BasicLayoutR image={bgImage} >
      <ToastContainer />
      <Card
        sx={{
          margin: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}>
        <img
          src={logo}
          alt="University Logo"
          style={{
            height: "120px",
            objectFit: "contain",
            borderRadius: "10px",
            marginBottom: 10,
            marginTop: 6,
          }}
        />
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          p={2}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Applicant Register
          </MDTypography>
        </MDBox>
        <MDBox pt={2} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                id="first_name"
                name="first_name"
                type="text"
                label="First Name *"
                value={first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value, setFirstName)}
                variant="standard"
                fullWidth
                error={!!formErrors.first_name}
              />
              {formErrors.first_name && (
                <FormHelperText error>
                  {formErrors.first_name}
                </FormHelperText>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                id="last_name"
                name="last_name"
                type="text"
                label="Last Name *"
                value={last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value, setLastName)}
                variant="standard"
                fullWidth
                error={!!formErrors.last_name}
              />
              {formErrors.last_name && (
                <FormHelperText error>
                  {formErrors.last_name}
                </FormHelperText>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                id="email"
                name="email"
                type="email"
                label="Email *"
                value={email}
                onChange={(e) => handleInputChange('email', e.target.value, setEmail)}
                variant="standard"
                fullWidth
                error={!!formErrors.email}
              />
              {formErrors.email && (
                <FormHelperText error>
                  {formErrors.email}
                </FormHelperText>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                id="cnic"
                name="cnic"
                type="text"
                label="CNIC *"
                value={cnic}
                onChange={handleCnicChange}
                variant="standard"
                fullWidth
                error={!!formErrors.cnic}
              />
              {formErrors.cnic && (
                <FormHelperText error>
                  {formErrors.cnic}
                </FormHelperText>
              )}
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                id="phone"
                name="phone"
                type="text"
                label="WhatsApp Number e.g. 03XXXXXXXXX *"
                value={phone}
                onChange={handlePhoneChange}
                variant="standard"
                fullWidth
                error={phoneError || !!formErrors.phone}
              />
              {(phoneError || formErrors.phone) && (
                <FormHelperText error>
                  {formErrors.phone || "WhatsApp number must start with 03 and be 11 digits long."}
                </FormHelperText>
              )}
            </MDBox>
            <MDBox mt={2}>
              <MDButton variant="gradient" color="info" fullWidth onClick={registerStudent}>
                {loading ? <CircularProgress color='inherit' size={20} /> : "Sign Up"}
              </MDButton>
            </MDBox>
            <MDBox mt={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Already have an account?{" "}
                <MDTypography
                  component={Link}
                  to={`/`}
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign In
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      {/* Success Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box sx={modalStyle}>
          <MDTypography id="success-modal-title" variant="h6" component="h2" mb={2}>
            Registration Successful
          </MDTypography>
          <MDTypography id="success-modal-description" variant="body2" mb={3}>
            {successMessage}
          </MDTypography>
          <MDButton variant="gradient" color="info" onClick={handleCloseModal}>
            OK
          </MDButton>
        </Box>
      </Modal>
    </BasicLayoutR>
  );
}

export default StudentRegister;
